/**
 * ContributionSettings Component
 * Frequency selector, contribution patterns, confidence threshold
 */

import { Activity, MessageCircle, Target, TrendingUp } from 'lucide-react';

interface ContributionSettingsProps {
  frequency: 'very_active' | 'active' | 'moderate' | 'occasional' | 'rare';
  pattern: 'helpful' | 'contrarian' | 'balanced' | 'supportive';
  confidenceThreshold: number;
  onFrequencyChange: (frequency: 'very_active' | 'active' | 'moderate' | 'occasional' | 'rare') => void;
  onPatternChange: (pattern: 'helpful' | 'contrarian' | 'balanced' | 'supportive') => void;
  onConfidenceThresholdChange: (threshold: number) => void;
}

const FREQUENCY_OPTIONS = [
  {
    value: 'very_active' as const,
    label: 'Cok Aktif',
    description: 'Neredeyse her posta katki saglar',
    icon: '🔥',
    color: '#ef4444'
  },
  {
    value: 'active' as const,
    label: 'Aktif',
    description: 'Cogu posta katki saglar',
    icon: '⚡',
    color: '#f59e0b'
  },
  {
    value: 'moderate' as const,
    label: 'Orta',
    description: 'Ilgili postlara katki saglar',
    icon: '💫',
    color: '#3b82f6'
  },
  {
    value: 'occasional' as const,
    label: 'Ara Sira',
    description: 'Sadece onemli postlara katki saglar',
    icon: '🌙',
    color: '#8b5cf6'
  },
  {
    value: 'rare' as const,
    label: 'Nadir',
    description: 'Cok ozel durumlarda katki saglar',
    icon: '⭐',
    color: '#64748b'
  }
];

const PATTERN_OPTIONS = [
  {
    value: 'helpful' as const,
    label: 'Yardimci',
    description: 'Her zaman cozum odakli ve yardimci',
    icon: '🤝',
    color: '#10b981'
  },
  {
    value: 'contrarian' as const,
    label: 'Elestirici',
    description: 'Alternatif bakis acilari sunar, sorgular',
    icon: '🤔',
    color: '#f59e0b'
  },
  {
    value: 'balanced' as const,
    label: 'Dengeli',
    description: 'Hem destekler hem elestiri yapar',
    icon: '⚖️',
    color: '#3b82f6'
  },
  {
    value: 'supportive' as const,
    label: 'Destekleyici',
    description: 'Cesaretlendirici ve pozitif',
    icon: '💪',
    color: '#ec4899'
  }
];

export default function ContributionSettings({
  frequency,
  pattern,
  confidenceThreshold,
  onFrequencyChange,
  onPatternChange,
  onConfidenceThresholdChange
}: ContributionSettingsProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Frequency Selector */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: '#3b82f615',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Activity size={20} color="#3b82f6" />
          </div>
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
              Katki Sikligi
            </h4>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
              Agent ne kadar sik katki saglayacak?
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          {FREQUENCY_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => onFrequencyChange(option.value)}
              style={{
                padding: '16px',
                borderRadius: '12px',
                border: frequency === option.value ? `2px solid ${option.color}` : '1px solid #e2e8f0',
                background: frequency === option.value ? `${option.color}08` : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{option.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c', marginBottom: '4px' }}>
                {option.label}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
                {option.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Pattern Selector */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: '#ec489915',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MessageCircle size={20} color="#ec4899" />
          </div>
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
              Katki Tarzi
            </h4>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
              Agent nasil bir yaklasim benimser?
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {PATTERN_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => onPatternChange(option.value)}
              style={{
                padding: '16px',
                borderRadius: '12px',
                border: pattern === option.value ? `2px solid ${option.color}` : '1px solid #e2e8f0',
                background: pattern === option.value ? `${option.color}08` : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{option.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c', marginBottom: '4px' }}>
                {option.label}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
                {option.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Confidence Threshold */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: '#10b98115',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Target size={20} color="#10b981" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
                Guven Esigi
              </h4>
              <span style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#10b981'
              }}>{Math.round(confidenceThreshold * 100)}%</span>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
              Agent en az bu guven seviyesinde oldugunda katki saglar
            </p>
          </div>
        </div>

        <div style={{ paddingLeft: '52px' }}>
          <input
            type="range"
            min="0"
            max="100"
            value={confidenceThreshold * 100}
            onChange={(e) => onConfidenceThresholdChange(parseInt(e.target.value) / 100)}
            style={{
              width: '100%',
              height: '8px',
              borderRadius: '4px',
              background: `linear-gradient(to right, #10b98130 0%, #10b98130 ${confidenceThreshold * 100}%, #e2e8f0 ${confidenceThreshold * 100}%, #e2e8f0 100%)`,
              outline: 'none',
              cursor: 'pointer',
              WebkitAppearance: 'none',
              appearance: 'none'
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px'
          }}>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Dusuk (0%)</span>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Yuksek (100%)</span>
          </div>
        </div>

        {/* Confidence Explanation */}
        <div style={{
          marginTop: '16px',
          padding: '16px',
          borderRadius: '12px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <TrendingUp size={16} color="#64748b" />
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a202c' }}>
              What is Trust Threshold?
            </span>
          </div>
          <p style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.6', margin: 0 }}>
            {confidenceThreshold < 0.5 && 'Dusuk esik: Agent daha sik katki saglar ama bazen yanlis olabilir.'}
            {confidenceThreshold >= 0.5 && confidenceThreshold < 0.7 && 'Orta esik: Dengeli bir yaklasim, cogu durumda dogru cevaplar.'}
            {confidenceThreshold >= 0.7 && confidenceThreshold < 0.85 && 'Yuksek esik: Agent sadece emin oldugunda katki saglar, daha az ama daha dogru.'}
            {confidenceThreshold >= 0.85 && 'Cok yuksek esik: Agent sadece cok emin oldugunda katki saglar, nadir ama neredeyse her zaman dogru.'}
          </p>
        </div>
      </div>
    </div>
  );
}
